import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Globe } from 'lucide-react';
import { Badge } from './ui/badge';
import { getAnalytics } from '../data/api';

const DEFAULT_PERIOD_DAYS = 30;

const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(DEFAULT_PERIOD_DAYS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getAnalytics(days).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [days]);

  if (loading || !data) {
    return <div className="text-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  }

  const sortedDays = Object.entries(data.byDay || {}).sort((a, b) => a[0].localeCompare(b[0]));
  const maxDayViews = Math.max(...sortedDays.map(d => d[1]), 1);

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-900">Website Analytics</h2>
        <div className="flex gap-2">
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => setDays(d)} data-testid={`analytics-period-${d}`}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${days === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >{d}d</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><BarChart3 className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900" data-testid="analytics-total-views">{data.totalViews}</p>
              <p className="text-xs text-gray-500">Total Views ({days}d)</p>
            </div>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900" data-testid="analytics-today-views">{data.todayViews}</p>
              <p className="text-xs text-gray-500">Today's Views</p>
            </div>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Globe className="w-5 h-5 text-purple-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{(data.topPages || []).length}</p>
              <p className="text-xs text-gray-500">Unique Pages</p>
            </div>
          </div>
        </div>
      </div>

      {sortedDays.length > 0 && (
        <div className="bg-white border rounded-xl p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Daily Page Views</h3>
          <div className="flex items-end gap-1 h-32">
            {sortedDays.map(([day, count]) => (
              <div key={day} className="flex-1 flex flex-col items-center gap-1 group relative">
                <div className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors min-h-[2px]" style={{ height: `${(count / maxDayViews) * 100}%` }} />
                <span className="text-[9px] text-gray-400 truncate w-full text-center">{day.slice(5)}</span>
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">{count} views</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border rounded-xl p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Top Pages</h3>
          <div className="space-y-2">
            {(data.topPages || []).map(([page, count], i) => (
              <div key={page} className="flex items-center gap-3">
                <span className="text-xs font-mono text-gray-400 w-5">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 truncate">{page}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{count}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                    <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${(count / (data.topPages[0]?.[1] || 1)) * 100}%` }} />
                  </div>
                </div>
              </div>
            ))}
            {(data.topPages || []).length === 0 && <p className="text-sm text-gray-400">No data yet</p>}
          </div>
        </div>

        <div className="bg-white border rounded-xl p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Traffic Sources</h3>
          <div className="space-y-2">
            {(data.topReferrers || []).map(([ref, count], i) => (
              <div key={ref} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-400 w-5">{i + 1}.</span>
                  <span className="text-sm text-gray-700">{ref}</span>
                </div>
                <Badge variant="secondary" className="text-xs">{count}</Badge>
              </div>
            ))}
            {(data.topReferrers || []).length === 0 && <p className="text-sm text-gray-400">No data yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;
